import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import { postRequest, getRequest } from '../../utils/axios';

import MainA from '../../layout/Main Admin';
import Image from "../../components/atoms/Image";
import Button from '../../components/atoms/Button';
import CustomDropdown from '../../components/atoms/CustomDropdown';
import CertificateViewer from '../../components/molecules/CertificateViewer';
import Loader from '../../components/atoms/Loader';
import Modal from '../../components/atoms/Modal';

import backArrow from '../../assets/icons/arrow-left.svg';

import './styles.scss';

const CategoryGallery = () => {
    const [state, setState] = useState({
        pageName: '',
        galleryYearMonthData: [],
        galleryByYearMonthData: [],
        selectedImages: [],
        selectedYear: '',
        availableMonths: []
    });

    const [loading, setLoading] = useState(false);
    const [selectedMonthLabel, setSelectedMonthLabel] = useState('');
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [pendingImage, setPendingImage] = useState(null);
    const [selectedRunType, setSelectedRunType] = useState('bib');
    const [textColor, setTextColor] = useState('#ffffff');
    const [colorSubmitting, setColorSubmitting] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewRunType, setPreviewRunType] = useState('bib');

    const { page } = useParams();
    const navigate = useNavigate();
    const imageUrl = "https://dynamicproductstorage.blob.core.windows.net/gallery";

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const isCertificate = queryParams.get('isCertificate') === 'true';
        const isBanner = queryParams.get('isBanner') === 'true';
        const pageNameFromURL = queryParams.get('pageName');

        if (pageNameFromURL) {
            setState((prev) => ({ ...prev, pageName: decodeURIComponent(pageNameFromURL) }));
        }

        fetchGalleryYearMonth();

        if (isBanner) {
            fetchBannerImage(page)
        }
        else if (isCertificate) {
            fetchCertificateImage(page);
        } else {
            fetchSelectedImages(page);
        }
    }, [page]);


    const fetchCertificateImage = async (pageId) => {
        setLoading(true);
        try {
            const resp = await getRequest(`EventCertificate/GetEventCertificateByEventId/${pageId}`);
            const data = resp.data || null;

            setTextColor(data[0]?.textColor || '#ffffff'); // set text color for certificate if available
            const normalize = (itm, defaultRunType = 'bib') => ({
                galleryId: itm.galleryId,
                galleryFilePath: itm.galleryFilePath.replace(/\\/g, '/'),
                galleryFileName: itm.galleryFileName,
                galleryFileTitle: itm.galleryFileTitle,
                runType: itm.runType || defaultRunType
            });

            let items = [];

            if (!data) {
                items = [];
            } else if (Array.isArray(data)) {
                items = data.map((d) => normalize(d));
            } else if (data && typeof data === 'object') {
                // API might return single object with runType or nested properties for bib/fun
                if (data.runType || data.galleryId) {
                    items = [normalize(data)];
                } else {
                    // try to find nested bib/fun keys
                    if (data.bib) items.push(normalize(data.bib, 'bib'));
                    if (data.fun) items.push(normalize(data.fun, 'fun'));
                }
            }

            if (items.length > 0) {
                setState((prev) => ({
                    ...prev,
                    selectedImages: items,
                }));
            }
        } catch (error) {
            NotificationManager.error('Failed to fetch certificate image');
            console.error('Error fetching certificate image:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBannerImage = async (pageId) => {
        setLoading(true);
        try {
            const resp = await getRequest(`EventBanner/GetEventBannerByEventId/${pageId}`);
            const data = resp.data || null;

            if (data) {
                setState((prev) => ({
                    ...prev,
                    selectedImages: [
                        {
                            galleryId: data.galleryId,
                            galleryFilePath: data.galleryFilePath.replace(/\\/g, '/'),
                            galleryFileName: data.galleryFileName,
                            galleryFileTitle: data.galleryFileTitle,
                        },
                    ],
                }));
            }
        } catch (error) {
            NotificationManager.error('Failed to fetch certificate image');
            console.error('Error fetching certificate image:', error);
        } finally {
            setLoading(false);
        }
    };


    const fetchGalleryYearMonth = async () => {
        setLoading(true);
        try {
            const resp = await getRequest('Gallery/GetGalleryYearMonth');
            const data = resp.data || [];
            setState(prev => ({ ...prev, galleryYearMonthData: data }));

            if (data.length > 0) {
                const latestYear = Math.max(...data.map(item => item.year));
                const monthsForLatestYear = data
                    .filter(item => item.year === latestYear)
                    .sort((a, b) => new Date(`01 ${a.month} ${a.year}`) - new Date(`01 ${b.month} ${b.year}`));

                const latestMonthItem = monthsForLatestYear[monthsForLatestYear.length - 1];

                if (latestMonthItem.galleryFilePath) {
                    setState(prev => ({
                        ...prev,
                        selectedYear: latestYear,
                        selectedMonth: latestMonthItem.galleryFilePath,
                        availableMonths: monthsForLatestYear.map(item => ({
                            value: item.galleryFilePath,
                            label: item.month
                        }))
                    }));
                    setSelectedMonthLabel(latestMonthItem.month);
                    fetchGalleryByYearMonth(latestMonthItem.galleryFilePath);
                }
            }
        } catch (error) {
            NotificationManager.error('Failed to fetch gallery year/month data');
        } finally {
            setLoading(false);
        }
    };

    const handleMonthSelection = (selectedOption) => {
        setState(prev => ({
            ...prev,
            selectedMonth: selectedOption.value,
        }));
        setSelectedMonthLabel(selectedOption.label);
        fetchGalleryByYearMonth(selectedOption.value);
    };

    const fetchGalleryByYearMonth = async (galleryFilePath) => {
        setLoading(true);
        try {
            const path = galleryFilePath.replaceAll("\\", "-");
            const resp = await getRequest(`Gallery/GetGalleryByYearMonth/${path}`);
            const galleryData = resp.data.map(rd => ({ ...rd, galleryFilePath }));
            setState(prev => ({ ...prev, galleryByYearMonthData: galleryData || [] }));
        } catch (error) {
            NotificationManager.error('Failed to fetch gallery data by year/month');
        } finally {
            setLoading(false);
        }
    };

    const fetchSelectedImages = async (pageId) => {
        setLoading(true);
        try {
            const resp = await getRequest(`PageGallery/GetPageGallery/${pageId}`);
            const data = resp.data || [];
            setState(prev => ({
                ...prev,
                selectedImages: data.map(img => ({
                    ...img,
                    galleryFilePath: img.galleryFilePath.replace(/\\/g, '/'),
                }))
            }));
        } catch (error) {
            NotificationManager.error('Failed to fetch selected images');
        } finally {
            setLoading(false);
        }
    };

    const handleImageSelection = (data) => {
        const queryParams = new URLSearchParams(window.location.search);
        const isCertificate = queryParams.get('isCertificate') === 'true';
        const isBanner = queryParams.get('isBanner') === 'true';

        if (isCertificate) {
            setPendingImage(data);
            setSelectedRunType('bib');
            setConfirmModalOpen(true);
            return;
        }

        if (isBanner) {
            setState((prev) => ({
                ...prev,
                selectedImages: [data],
                galleryByYearMonthData: prev.galleryByYearMonthData.filter((image) => image.galleryId !== data.galleryId)
            }));

            (async () => {
                try {
                    const payload = { galleryId: data.galleryId, pageId: Number(page) };
                    const response = await postRequest('EventBanner/PostEventBanner', payload);
                    if (response.status === 200 || response.status === 201) {
                        NotificationManager.success('Banner image saved successfully');
                        // refresh banner/certificate selection from server
                        fetchBannerImage(page);
                    } else {
                        NotificationManager.error('Failed to save banner image');
                    }
                } catch (error) {
                    NotificationManager.error('An error occurred while saving the banner image');
                    console.error('Error posting banner image:', error);
                }
            })();

            return;
        }

        if (!state.selectedImages.some((image) => image.galleryId === data.galleryId)) {
            setState((prev) => ({
                ...prev,
                selectedImages: [...prev.selectedImages, data],
                galleryByYearMonthData: prev.galleryByYearMonthData.filter((image) => image.galleryId !== data.galleryId)
            }));
        }
    };

    const confirmImageSelection = async () => {
        if (!pendingImage) {
            setConfirmModalOpen(false);
            return;
        }

        const data = pendingImage;
        const queryParams = new URLSearchParams(window.location.search);
        const isCertificate = queryParams.get('isCertificate') === 'true';

        if (!isCertificate) {
            setConfirmModalOpen(false);
            setPendingImage(null);
            return;
        }

        setState((prev) => ({
            ...prev,
            selectedImages: [{ ...data, runType: selectedRunType }],
            galleryByYearMonthData: prev.galleryByYearMonthData.filter((image) => image.galleryId !== data.galleryId)
        }));

        try {
            const payload = {
                galleryId: data.galleryId,
                pageId: Number(page),
                runType: selectedRunType
            };

            const response = await postRequest('EventCertificate/PostEventCertificate', payload);
            if (response.status === 200 || response.status === 201) {
                NotificationManager.success('Certificate image saved successfully');
                // refresh certificate selection from server
                fetchCertificateImage(page);
            } else {
                NotificationManager.error('Failed to save certificate image');
            }
        } catch (error) {
            NotificationManager.error('An error occurred while saving the certificate image');
            console.error('Error posting certificate image:', error);
        }

        setConfirmModalOpen(false);
        setPendingImage(null);
    };

    const handleImageDeselection = (image) => {
        setState(prev => ({
            ...prev,
            selectedImages: prev.selectedImages.filter(selected => selected.galleryId !== image.galleryId),
            galleryByYearMonthData: [...prev.galleryByYearMonthData, image]
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams(window.location.search);
            const isCertificate = queryParams.get('isCertificate') === 'true';
            const isBanner = queryParams.get('isBanner') === 'true';

            if (!isCertificate && !isBanner) {
                const payload = state.selectedImages.map((data) => ({
                    clientId: 0,
                    pageId: Number(page),
                    galleryId: data.galleryId,
                }));
                await postRequest('PageGallery/PostPageGallery', payload);
                NotificationManager.success('Images saved successfully');
            }
        } catch (error) {
            NotificationManager.error('Failed to save images');
            console.error('Error saving images:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTextColor = async () => {
        setColorSubmitting(true);
        try {
            const payload = { textColor };
            const resp = await postRequest(`EventCertificate/UpdateTextColor/${page}`, payload);
            if (resp?.status === 200 || resp?.status === 201) {
                NotificationManager.success('Text color updated');
            } else {
                NotificationManager.error('Failed to update text color');
            }
        } catch (err) {
            console.error(err);
            NotificationManager.error('Failed to update text color');
        } finally {
            setColorSubmitting(false);
        }
    };

    const handleYearSelection = (selectedOption) => {
        const selectedYear = selectedOption.value;
        const monthsForSelectedYear = state.galleryYearMonthData
            .filter(item => item.year === selectedYear)
            .map(item => ({
                value: item.galleryFilePath,
                label: item.month
            }));
        setState(prev => ({
            ...prev,
            selectedYear,
            availableMonths: monthsForSelectedYear
        }));
    };

    const trimText = (text, maxLength) => text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

    const queryParamsForRender = new URLSearchParams(window.location.search);
    const isCertificateRender = queryParamsForRender.get('isCertificate') === 'true';
    const isBannerRender = queryParamsForRender.get('isBanner') === 'true';

    const hasCertificate = isCertificateRender && Array.isArray(state.selectedImages) && state.selectedImages.length > 0;

    const bibImages = state.selectedImages.filter(i => (i.runType || '').toString().toLowerCase() === 'bib');
    const funImages = state.selectedImages.filter(i => {
        const rt = (i.runType || '').toString().toLowerCase();
        return rt === 'fun';
    });

    return (
        <MainA active="expense-head-admin" className="b-event-gallery">
            <div className="gallery-wrapper">
                <div className="heading-wrapper">
                    <h1>{state?.pageName}</h1>
                    <div className='clickables'>
                        <CustomDropdown
                            placeholder="Select Year"
                            options={Array.from(new Set(state.galleryYearMonthData.map(item => item.year)))
                                .map(year => ({ value: year, label: year.toString() }))}
                            onSelect={handleYearSelection}
                            value={{ value: state.selectedYear, label: state.selectedYear.toString() }}
                        />
                        <CustomDropdown
                            placeholder="Select Month"
                            options={state.availableMonths}
                            onSelect={handleMonthSelection}
                            value={state.selectedMonth && selectedMonthLabel
                                ? { value: state.selectedMonth, label: selectedMonthLabel }
                                : null}
                            disabled={!state.selectedYear}
                        />

                        <Button onClick={() => navigate(-1)}> <img src={backArrow} alt='back arrow' /> Back</Button>
                    </div>
                </div>

                <div className='wrapper'>
                    <div className="gallery-display">
                        {loading ? (
                            <Loader loading={loading} />
                        ) : (
                            <>
                                <div className="detail">
                                    {state.selectedYear && selectedMonthLabel ? (
                                        <div className="year-section">
                                            <h2>{selectedMonthLabel} {state.selectedYear}</h2>
                                        </div>
                                    ) : (
                                        <div className="year-section">
                                            <h2>Please select a year and month</h2>
                                        </div>
                                    )}
                                </div>

                                <div className='all-images'>
                                    {state.galleryByYearMonthData.length > 0 ? state.galleryByYearMonthData.map(wnd => (
                                        <div
                                            key={wnd.galleryId}
                                            className="image"
                                            onClick={() => handleImageSelection(wnd)}
                                        >
                                            <Image
                                                src={`${imageUrl}/${wnd.galleryFilePath.replace(/\\/g, '/')}${wnd.galleryFileName}`}
                                            />
                                            <div className="file-path-cell" title={wnd.galleryFileTitle}>
                                                {trimText(wnd.galleryFileTitle, 15)}
                                            </div>
                                        </div>
                                    )) : (
                                        <div className='please-select-text'>
                                            Please select Year/Month to view images
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="selected-images-wrapper">
                        <div className="selected-images">
                            <h2>Selected Images ({state.selectedImages.length})</h2>
                            <div className="selected-list">
                                {isCertificateRender ? (
                                    <>
                                        <div className="selected-group">
                                            <h3>Bib Images ({bibImages.length})</h3>
                                            {bibImages.length > 0 ? (
                                                bibImages.map(selectedImage => (
                                                    <div
                                                        key={selectedImage.galleryId}
                                                        className="selected-thumbnail-item"
                                                        onClick={() => handleImageDeselection(selectedImage)}
                                                    >
                                                        <Image
                                                            src={`${imageUrl}/${selectedImage.galleryFilePath}${selectedImage.galleryFileName}`}
                                                            alt={selectedImage.galleryFileTitle}
                                                        />
                                                    </div>
                                                ))
                                            ) : (
                                                <p>No bib images selected.</p>
                                            )}
                                        </div>

                                        <div className="selected-group">
                                            <h3>Fun Images ({funImages.length})</h3>
                                            {funImages.length > 0 ? (
                                                funImages.map(selectedImage => (
                                                    <div
                                                        key={selectedImage.galleryId}
                                                        className="selected-thumbnail-item"
                                                        onClick={() => handleImageDeselection(selectedImage)}
                                                    >
                                                        <Image
                                                            src={`${imageUrl}/${selectedImage.galleryFilePath}${selectedImage.galleryFileName}`}
                                                            alt={selectedImage.galleryFileTitle}
                                                        />
                                                    </div>
                                                ))
                                            ) : (
                                                <p>No fun images selected.</p>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    // non-certificate: show single combined selected images list
                                    state.selectedImages.length > 0 ? (
                                        state.selectedImages.map(selectedImage => (
                                            <div
                                                key={selectedImage.galleryId}
                                                className="selected-thumbnail-item"
                                                onClick={() => handleImageDeselection(selectedImage)}
                                            >
                                                <Image
                                                    src={`${imageUrl}/${selectedImage.galleryFilePath}${selectedImage.galleryFileName}`}
                                                    alt={selectedImage.galleryFileTitle}
                                                />
                                            </div>
                                        ))
                                    ) : (
                                        <p>No images selected.</p>
                                    )
                                )}
                            </div>
                            {!['true'].includes(new URLSearchParams(window.location.search).get('isCertificate')) &&
                                !['true'].includes(new URLSearchParams(window.location.search).get('isBanner')) && (
                                    <Button onClick={handleSubmit}>Save</Button>
                                )}
                        </div>

                        {isCertificateRender && <div className="color-wrapper">
                            <div className="color-card">
                                <div className="color-card-header">
                                    <h3>Certificate Text Color</h3>
                                    <p>Pick a color for the certificate text to improve readability.</p>
                                </div>
                                <div className="color-card-body">
                                    <div className="picker-row">
                                        <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="color-input" />
                                        <div className="hex-value">{textColor.toUpperCase()}</div>
                                    </div>
                                    <div className="actions-row">
                                        <div className="preview-controls" style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 8 }}>
                                            <select value={previewRunType} onChange={(e) => setPreviewRunType(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6 }}>
                                                <option value="bib">Bib</option>
                                                <option value="fun">Fun</option>
                                            </select>
                                            <Button onClick={() => setPreviewOpen(true)} disabled={!hasCertificate}>Preview</Button>
                                        </div>
                                        <Button onClick={handleUpdateTextColor} disabled={colorSubmitting || !hasCertificate}>
                                            {colorSubmitting ? 'Saving...' : 'Save Color'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>}
                    </div>
                </div>
            </div>
            <Modal isOpen={confirmModalOpen} closeModal={() => { setConfirmModalOpen(false); setPendingImage(null); }}>
                <div className="confirm-selection-modal">
                    <h3>Confirm Image Selection</h3>
                    <p>Are you sure you want to select this image?</p>

                    <div className="run-type-options">
                        <label style={{ marginRight: 12 }}>
                            <input
                                type="radio"
                                name="runtype"
                                value="bib"
                                checked={selectedRunType === 'bib'}
                                onChange={() => setSelectedRunType('bib')}
                            />
                            &nbsp;Bib
                        </label>

                        <label>
                            <input
                                type="radio"
                                name="runtype"
                                value="fun"
                                checked={selectedRunType === 'fun'}
                                onChange={() => setSelectedRunType('fun')}
                            />
                            &nbsp;Fun
                        </label>
                    </div>

                    <div className="modal-actions" style={{ marginTop: 16 }}>
                        <Button onClick={confirmImageSelection}>Confirm</Button>
                        <Button onClick={() => { setConfirmModalOpen(false); setPendingImage(null); }} style={{ marginLeft: 8 }}>Cancel</Button>
                    </div>
                </div>
            </Modal>
            {previewOpen && (
                <CertificateViewer pageId={page} runType={previewRunType} textColor={textColor} onClose={() => setPreviewOpen(false)} />
            )}
        </MainA>
    );
};

export default CategoryGallery;
